import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { sendNewsletterSignup } from '@/lib/dopplerService.js';

const OPEN_DELAY_MS = 3000;

function HomeNewsletterPopup() {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({ nombre: '', email: '' });
  const [website, setWebsite] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const dialogRef = useRef(null);

  useEffect(() => {
    let openTimer;

    const handleFirstScroll = () => {
      window.removeEventListener('scroll', handleFirstScroll);
      openTimer = window.setTimeout(() => setIsOpen(true), OPEN_DELAY_MS);
    };

    window.addEventListener('scroll', handleFirstScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleFirstScroll);
      window.clearTimeout(openTimer);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);
    window.requestAnimationFrame(() => dialogRef.current?.focus());

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
    if (feedback) setFeedback(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (website) {
      setIsOpen(false);
      return;
    }

    if (!formData.email) {
      const message = 'Ingresá tu email para suscribirte.';
      setFeedback({ type: 'error', message });
      toast.error(message);
      return;
    }

    if (!acceptedTerms) {
      const message = 'Necesitás aceptar las comunicaciones para continuar.';
      setFeedback({ type: 'error', message });
      toast.error(message);
      return;
    }

    setIsSubmitting(true);
    setFeedback(null);

    try {
      await sendNewsletterSignup(formData);
      const message = '¡Listo! Ya estás suscripta al newsletter.';
      setFeedback({ type: 'success', message });
      toast.success(message);
      setFormData({ nombre: '', email: '' });
      setWebsite('');
      setAcceptedTerms(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'No pudimos registrar tu suscripción. Intentá nuevamente.';
      setFeedback({ type: 'error', message });
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="newsletter-popup-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) setIsOpen(false);
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="newsletter-popup-title"
        tabIndex={-1}
        className="newsletter-popup-dialog"
      >
        <button
          type="button"
          onClick={() => setIsOpen(false)}
          className="newsletter-popup-close"
          aria-label="Cerrar suscripción al newsletter"
        >
          <X aria-hidden="true" />
        </button>

        <div className="newsletter-popup-heading">
          <p className="newsletter-popup-eyebrow">Suscribite y recibí</p>
          <p className="newsletter-popup-discount">10% OFF en tu primer curso</p>
          <span className="newsletter-popup-plus" aria-hidden="true">+</span>
          <h2 id="newsletter-popup-title">&quot;La ruta del sueño ideal&quot;</h2>
          <p className="newsletter-popup-copy">
            La guía gratis para que tu bebé (y vos) duerman mejor. Además recibí promos y tips exclusivos todas las semanas.
          </p>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className="hidden" aria-hidden="true">
            <label htmlFor="popup-newsletter-website">Sitio web</label>
            <input
              id="popup-newsletter-website"
              value={website}
              onChange={(event) => setWebsite(event.target.value)}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          <div className="newsletter-popup-fields">
            <label>
              <span className="sr-only">Nombre</span>
              <input
                name="nombre"
                type="text"
                value={formData.nombre}
                onChange={handleChange}
                placeholder="Nombre"
                autoComplete="name"
                disabled={isSubmitting}
              />
            </label>
            <label>
              <span className="sr-only">Email</span>
              <input
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Email *"
                autoComplete="email"
                inputMode="email"
                required
                disabled={isSubmitting}
              />
            </label>
          </div>

          <button type="submit" disabled={isSubmitting} className="newsletter-popup-cta">
            {isSubmitting ? 'Suscribiendo...' : 'Quiero mi guía + 10% OFF'}
          </button>

          <label className="newsletter-popup-consent">
            <input
              type="checkbox"
              checked={acceptedTerms}
              onChange={(event) => setAcceptedTerms(event.target.checked)}
              disabled={isSubmitting}
            />
            <span>
              Acepto recibir comunicaciones y puedo darme de baja cuando quiera ·{' '}
              <Link to="/privacidad" onClick={() => setIsOpen(false)}>ver políticas</Link>
            </span>
          </label>

          {feedback && (
            <p className={`newsletter-popup-feedback ${feedback.type}`} role="status" aria-live="polite">
              {feedback.message}
            </p>
          )}
        </form>
      </div>
    </div>
  );
}

export default HomeNewsletterPopup;
